import { useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import type { SickLeaveImportPreviewResponse, SickLeaveImportRow } from '@ip/shared-types';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useHrApi, SessionExpiredError } from '@/services/api';
import { formatDateRange } from '@/lib/hr';
import { t } from '@/i18n';

type Step = 'upload' | 'preview' | 'done';

interface PueImportWizardProps {
  nip: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: () => void;
}

/**
 * PUE / eZUS report import wizard: upload → server-side dry-run preview with
 * matched / unmatched / conflict rows (name & contract mismatches are the
 * norm — manual reconciliation is required) → the user confirms rows → the
 * file is re-sent with the confirmed row indices (the backend's two-phase
 * contract) → per-row results.
 */
export function PueImportWizard({ nip, open, onOpenChange, onImported }: PueImportWizardProps) {
  const { previewSickLeaveImport, commitSickLeaveImport } = useHrApi();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<SickLeaveImportPreviewResponse | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);

  const reset = () => {
    setStep('upload');
    setFile(null);
    setBusy(false);
    setError(null);
    setPreview(null);
    setSelected(new Set());
    setResult(null);
  };

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) reset();
  };

  const allRows = useMemo(() => {
    if (!preview) return [];
    return [...preview.matched, ...preview.conflicts, ...preview.unmatched]
      .sort((a, b) => a.rowIndex - b.rowIndex);
  }, [preview]);

  const selectable = (row: SickLeaveImportRow) =>
    row.matchType !== 'none' && !row.error && !row.alreadyImported;

  const handleFile = async (picked: File) => {
    setFile(picked);
    setBusy(true);
    setError(null);
    try {
      const res = await previewSickLeaveImport(nip, picked);
      setPreview(res);
      // Clean matches pre-checked; conflicts require a conscious tick (art. 166).
      setSelected(new Set(res.matched.filter(selectable).filter((r) => !r.conflict).map((r) => r.rowIndex)));
      setStep('preview');
    } catch (e) {
      if (!(e instanceof SessionExpiredError)) {
        setError(e instanceof Error ? e.message : String(e));
      }
      setFile(null);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleCommit = async () => {
    if (!file || selected.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await commitSickLeaveImport(nip, file, [...selected].sort((a, b) => a - b));
      const created = res.results.filter((r) => r.outcome === 'created').length;
      const skipped = res.results.length - created;
      setResult({ created, skipped });
      setStep('done');
      toast.success(t('hr.pue.done', { created, skipped }));
      onImported();
    } catch (e) {
      if (!(e instanceof SessionExpiredError)) {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const toggle = (rowIndex: number) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  };

  const matchLabel = (row: SickLeaveImportRow) =>
    row.matchType === 'pesel'
      ? t('hr.pue.matchPesel')
      : row.matchType === 'name_dob'
        ? t('hr.pue.matchNameDob')
        : t('hr.pue.matchNone');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('hr.pue.title')}</DialogTitle>
          <DialogDescription>{t('hr.pue.step1')}</DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
              className="hover:border-primary/50 hover:bg-accent/30 flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed p-8 transition-colors"
            >
              {busy ? (
                <>
                  <Loader2 className="text-muted-foreground size-8 animate-spin" />
                  <span className="text-muted-foreground text-sm">{t('hr.pue.parsing')}</span>
                </>
              ) : (
                <>
                  <Upload className="text-muted-foreground size-8" />
                  <span className="text-sm font-medium">{t('hr.pue.selectFile')}</span>
                  <span className="text-muted-foreground text-xs">CSV / XLSX</span>
                </>
              )}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const picked = e.target.files?.[0];
                if (picked) handleFile(picked);
              }}
            />
          </div>
        )}

        {step === 'preview' && preview && (
          <>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 font-medium text-emerald-700 dark:text-emerald-400">
                {t('hr.pue.matched')}: {preview.matched.length}
              </span>
              <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-medium text-amber-700 dark:text-amber-400">
                {t('hr.pue.conflicts')}: {preview.conflicts.length}
              </span>
              <span className="rounded-full border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 font-medium text-rose-700 dark:text-rose-400">
                {t('hr.pue.unmatched')}: {preview.unmatched.length}
              </span>
            </div>

            <div className="min-h-0 flex-1 overflow-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>{t('hr.sick.employee')}</TableHead>
                    <TableHead>PESEL</TableHead>
                    <TableHead>{t('hr.sick.period')}</TableHead>
                    <TableHead>{t('hr.sick.series')}</TableHead>
                    <TableHead>{t('hr.sick.code')}</TableHead>
                    <TableHead>{t('hr.pue.matchCol')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allRows.map((row) => {
                    const canPick = selectable(row);
                    const name =
                      row.matchedEmployeeName ??
                      ([row.lastName, row.firstName].filter(Boolean).join(' ') || '—');
                    return (
                      <TableRow key={row.rowIndex} className={cn(!canPick && 'opacity-60')}>
                        <TableCell>
                          <Checkbox
                            checked={selected.has(row.rowIndex)}
                            disabled={!canPick}
                            onCheckedChange={() => toggle(row.rowIndex)}
                            aria-label={name}
                          />
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm">{name}</TableCell>
                        <TableCell className="text-muted-foreground font-mono text-xs">{row.pesel ?? '—'}</TableCell>
                        <TableCell className="whitespace-nowrap text-sm">
                          {row.dateFrom && row.dateTo ? formatDateRange(row.dateFrom, row.dateTo) : '—'}
                        </TableCell>
                        <TableCell className="text-muted-foreground font-mono text-xs">{row.seriesNumber ?? '—'}</TableCell>
                        <TableCell className="text-sm">{row.letterCode ?? '—'}</TableCell>
                        <TableCell className="text-xs">
                          {row.error ? (
                            <span className="text-rose-600 dark:text-rose-400">{t('hr.pue.rowError', { error: row.error })}</span>
                          ) : row.alreadyImported ? (
                            <span className="text-muted-foreground">{t('hr.pue.alreadyImported')}</span>
                          ) : row.matchType === 'none' ? (
                            <span className="text-rose-600 dark:text-rose-400">{t('hr.pue.matchNone')}</span>
                          ) : row.conflict ? (
                            <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                              <AlertTriangle className="size-3.5 shrink-0" />
                              {t('hr.pue.conflictNote')}
                            </span>
                          ) : (
                            <span className="text-emerald-600 dark:text-emerald-400">{matchLabel(row)}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {step === 'done' && result && (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <CheckCircle2 className="size-10 text-emerald-500" />
            <p className="text-sm">{t('hr.pue.done', { created: result.created, skipped: result.skipped })}</p>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          {step === 'preview' && (
            <>
              <Button variant="outline" disabled={busy} onClick={reset}>
                <FileSpreadsheet className="size-4" />
                {t('hr.pue.back')}
              </Button>
              <Button disabled={busy || selected.size === 0} onClick={handleCommit}>
                {busy && <Loader2 className="size-4 animate-spin" />}
                {t('hr.pue.commit', { count: selected.size })}
              </Button>
            </>
          )}
          {(step === 'upload' || step === 'done') && (
            <Button variant={step === 'done' ? 'default' : 'outline'} onClick={() => handleOpenChange(false)}>
              {step === 'done' ? t('hr.pue.close') : t('hr.pue.cancel')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
